<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import SLAFilter from '../SLA/SLAFilter.vue';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import NextSelect from 'dashboard/components-next/select/Select.vue';
import { subDays, fromUnixTime } from 'date-fns';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';
import {
  generateReportURLParams,
  parseReportURLParams,
  parseFilterURLParams,
} from '../../helpers/reportFilterHelper';

const emit = defineEmits(['filterChange']);

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

// Corte de ciclos curtos (ex.: o "obrigado" que reabre e é resolvido em um
// minuto). Fica no localStorage como conveniência de quem usa o relatório;
// vazio = sem corte. O escopo diz se vale para todos os ciclos ou só para
// reaberturas (ciclo 2 em diante).
const CYCLE_FILTER_STORAGE_KEY = 'sla_report_cycle_filter';
const CYCLE_SCOPE_OPTIONS = ['reopened', 'all'];

const readStoredCycleFilter = () => {
  try {
    const stored = JSON.parse(
      window.localStorage.getItem(CYCLE_FILTER_STORAGE_KEY) || '{}'
    );
    return {
      minutes:
        Number.isFinite(Number(stored.minutes)) && Number(stored.minutes) > 0
          ? Number(stored.minutes)
          : '',
      scope: CYCLE_SCOPE_OPTIONS.includes(stored.scope)
        ? stored.scope
        : 'reopened',
    };
  } catch (error) {
    return { minutes: '', scope: 'reopened' };
  }
};

const storedCycleFilter = readStoredCycleFilter();
const minCycleMinutes = ref(storedCycleFilter.minutes);
const minCycleScope = ref(storedCycleFilter.scope);

const cycleScopeOptions = computed(() => [
  { value: 'reopened', label: t('SLA_REPORTS.CYCLE_FILTER.SCOPE.REOPENED') },
  { value: 'all', label: t('SLA_REPORTS.CYCLE_FILTER.SCOPE.ALL') },
]);

const cycleFilterParams = computed(() => {
  const minutes = Number(minCycleMinutes.value);
  if (!Number.isFinite(minutes) || minutes <= 0) {
    return { min_cycle_minutes: null, min_cycle_scope: null };
  }
  return { min_cycle_minutes: minutes, min_cycle_scope: minCycleScope.value };
});

const persistCycleFilter = () => {
  try {
    window.localStorage.setItem(
      CYCLE_FILTER_STORAGE_KEY,
      JSON.stringify({
        minutes: cycleFilterParams.value.min_cycle_minutes,
        scope: minCycleScope.value,
      })
    );
  } catch (error) {
    // armazenamento indisponível: o filtro vale só nesta visita
  }
};

// Initialize from URL params immediately
const urlParams = parseReportURLParams(route.query);
const initialDateRange =
  urlParams.from && urlParams.to
    ? [fromUnixTime(urlParams.from), fromUnixTime(urlParams.to)]
    : [subDays(new Date(), 6), new Date()];

const selectedDateRange = ref(urlParams.range || 'last7days');
const selectedGroupByFilter = ref(null);
const customDateRange = ref(initialDateRange);

const to = computed(() => getUnixEndOfDay(customDateRange.value[1]));
const from = computed(() => getUnixStartOfDay(customDateRange.value[0]));

const updateURLParams = () => {
  const dateParams = generateReportURLParams({
    from: from.value,
    to: to.value,
    range: selectedDateRange.value,
  });

  const filterParams = parseFilterURLParams(route.query);
  const params = { ...dateParams };

  if (filterParams.agent_id) params.agent_id = filterParams.agent_id;
  if (filterParams.inbox_id) params.inbox_id = filterParams.inbox_id;
  if (filterParams.team_id) params.team_id = filterParams.team_id;
  if (filterParams.sla_policy_id)
    params.sla_policy_id = filterParams.sla_policy_id;
  if (filterParams.label) params.label = filterParams.label;

  router.replace({ query: params });
};

const emitChange = () => {
  updateURLParams();
  emit('filterChange', {
    from: from.value,
    to: to.value,
    ...selectedGroupByFilter.value,
    ...cycleFilterParams.value,
  });
};

const emitFilterChange = params => {
  selectedGroupByFilter.value = params;
  emit('filterChange', {
    from: from.value,
    to: to.value,
    ...selectedGroupByFilter.value,
    ...cycleFilterParams.value,
  });
};

const onCycleFilterChange = () => {
  persistCycleFilter();
  emit('filterChange', {
    from: from.value,
    to: to.value,
    ...selectedGroupByFilter.value,
    ...cycleFilterParams.value,
  });
};

const onDateRangeChange = ([startDate, endDate, rangeType]) => {
  customDateRange.value = [startDate, endDate];
  selectedDateRange.value = rangeType;
  emitChange();
};

const setInitialRange = () => {
  customDateRange.value = [subDays(new Date(), 6), new Date()];
  emitChange();
};

onMounted(() => {
  if (!route.query.from || !route.query.to) {
    setInitialRange();
  }
});
</script>

<template>
  <div class="flex flex-col flex-wrap w-full gap-3 md:flex-row">
    <WootDatePicker
      v-model:date-range="customDateRange"
      v-model:range-type="selectedDateRange"
      @date-range-changed="onDateRangeChange"
    />
    <SLAFilter @filter-change="emitFilterChange" />
    <div
      class="flex flex-wrap items-center gap-2 md:ltr:ml-auto md:rtl:mr-auto"
      :title="$t('SLA_REPORTS.CYCLE_FILTER.HELP')"
    >
      <span class="text-sm text-n-slate-11 whitespace-nowrap">
        {{ $t('SLA_REPORTS.CYCLE_FILTER.LABEL') }}
      </span>
      <Input
        v-model="minCycleMinutes"
        type="number"
        min="0"
        size="sm"
        custom-input-class="!w-20"
        :placeholder="$t('SLA_REPORTS.CYCLE_FILTER.MINUTES_PLACEHOLDER')"
        @change="onCycleFilterChange"
      />
      <span class="text-sm text-n-slate-11 whitespace-nowrap">
        {{ $t('SLA_REPORTS.CYCLE_FILTER.MINUTES_SUFFIX') }}
      </span>
      <NextSelect
        v-model="minCycleScope"
        :options="cycleScopeOptions"
        :aria-label="$t('SLA_REPORTS.CYCLE_FILTER.SCOPE.LABEL')"
        @change="onCycleFilterChange"
      />
    </div>
  </div>
</template>
